import React, { useState, useEffect } from "react";
import { Layout, Avatar, Dropdown } from "antd";
import { useNavigate, useLocation, Outlet } from "react-router-dom";
import {
  AppstoreOutlined,
  HomeOutlined,
  UserOutlined,
  SafetyCertificateOutlined,
  FileTextOutlined,
  KeyOutlined,
  BarChartOutlined,
  SettingOutlined,
  ArrowLeftOutlined,
  ExclamationCircleOutlined,
  TeamOutlined,
  CalendarOutlined,
  AuditOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  LogoutOutlined,
} from "@ant-design/icons";
import { useTenant } from "../../../store/context/tenant-context";
import { darkenHex, hexToRgba } from "../../../utils/helper/colorUtils";
import { UltimateAgentProvider, useUltimateAgent, UA_ROLES } from "../context/UltimateAgentContext";
import { useDashboardLayout, DASHBOARD_LAYOUTS } from "../../../store/context/dashboard-layout-context";
import { getSessionData } from "../../../utils/interceptors/localStorageService";
import DashboardLayoutSwitcher from "../../../components/Dashboard/DashboardLayoutSwitcher";

const { Sider, Content, Header } = Layout;

const getNavItems = (role) => {
  const overview = { key: "/ultimate-agent", icon: <AppstoreOutlined />, label: "Overview", exact: true };

  if (role === "TENANT") {
    return [
      overview,
      { type: "divider", label: "MY TENANCY" },
      { key: "/ultimate-agent/applications",  icon: <UserOutlined />,    label: "My Application" },
      { key: "/ultimate-agent/contracts",     icon: <FileTextOutlined />,label: "My Contract" },
      { key: "/ultimate-agent/tenancies",     icon: <KeyOutlined />,     label: "My Tenancy" },
      { type: "divider", label: "PAYMENTS" },
      { key: "/ultimate-agent/collections",   icon: <CalendarOutlined />,label: "Payment History" },
      { type: "divider", label: "" },
      { key: "/ultimate-agent/settings",      icon: <SettingOutlined />, label: "Settings" },
    ];
  }

  if (role === "AGENT") {
    return [
      overview,
      { type: "divider", label: "MANAGE" },
      { key: "/ultimate-agent/properties",    icon: <HomeOutlined />,              label: "Properties" },
      { key: "/ultimate-agent/applications",  icon: <UserOutlined />,              label: "Applications",          badge: 3 },
      { key: "/ultimate-agent/verifications", icon: <SafetyCertificateOutlined />, label: "Verification Packages" },
      { key: "/ultimate-agent/contracts",     icon: <FileTextOutlined />,          label: "Contracts" },
      { key: "/ultimate-agent/tenancies",     icon: <KeyOutlined />,               label: "Tenancies" },
      { type: "divider", label: "FINANCE" },
      { key: "/ultimate-agent/collections",   icon: <CalendarOutlined />,          label: "Collections" },
      { key: "/ultimate-agent/arrears",       icon: <ExclamationCircleOutlined />, label: "Arrears",               badge: 2 },
      { type: "divider", label: "" },
      { key: "/ultimate-agent/reports",       icon: <BarChartOutlined />,          label: "Reports" },
      { key: "/ultimate-agent/settings",      icon: <SettingOutlined />,           label: "Settings" },
    ];
  }

  // LANDLORD (default) — full access
  return [
    overview,
    { type: "divider", label: "MANAGE" },
    { key: "/ultimate-agent/properties",    icon: <HomeOutlined />,              label: "Properties & Units" },
    { key: "/ultimate-agent/applications",  icon: <UserOutlined />,              label: "Applications",          badge: 3 },
    { key: "/ultimate-agent/verifications", icon: <SafetyCertificateOutlined />, label: "Verification Packages" },
    { key: "/ultimate-agent/contracts",     icon: <FileTextOutlined />,          label: "Contracts" },
    { key: "/ultimate-agent/tenancies",     icon: <KeyOutlined />,               label: "Tenancies" },
    { type: "divider", label: "FINANCE" },
    { key: "/ultimate-agent/collections",   icon: <CalendarOutlined />,          label: "Collections" },
    { key: "/ultimate-agent/arrears",       icon: <ExclamationCircleOutlined />, label: "Arrears",               badge: 2 },
    { key: "/ultimate-agent/reconciliation",icon: <AuditOutlined />,             label: "Reconciliation" },
    { type: "divider", label: "SETTINGS" },
    { key: "/ultimate-agent/team",          icon: <TeamOutlined />,              label: "Team & Roles" },
    { key: "/ultimate-agent/reports",       icon: <BarChartOutlined />,          label: "Reports" },
    { key: "/ultimate-agent/settings",      icon: <SettingOutlined />,           label: "Settings" },
  ];
};

const UltimateAgentShell = () => {
  const navigate    = useNavigate();
  const location    = useLocation();
  const tenant      = useTenant();
  const { activeRole, setActiveRole } = useUltimateAgent();
  const { layout }                    = useDashboardLayout();
  const primary     = tenant?.theme?.colorPrimary || "#6366f1";
  const darkPrimary = darkenHex(primary, 0.72);
  const [collapsed, setCollapsed] = useState(false);
  const [session,   setSession]   = useState(null);

  useEffect(() => {
    const data = getSessionData();
    if (data) setSession(data);
  }, []);

  const userInitials  = `${(session?.firstName || "")[0] || ""}${(session?.lastName || "")[0] || ""}`.toUpperCase() || "U";
  const displayName   = session?.displayName || `${session?.firstName || ""} ${session?.lastName || ""}`.trim() || "User";
  const emailAddress  = session?.emailAddress || "";

  const navItems = getNavItems(activeRole);

  const activeKey = (() => {
    const exact = navItems.find((i) => i.exact && i.key === location.pathname);
    if (exact) return exact.key;
    return [...navItems]
      .filter((i) => !i.type && !i.exact)
      .sort((a, b) => b.key.length - a.key.length)
      .find((i) => location.pathname.startsWith(i.key))?.key || "/ultimate-agent";
  })();

  // ── Light sidebar in Classic mode (mirrors banking) ───────────────────────
  const isLightSidebar = layout === DASHBOARD_LAYOUTS.DEFAULT;

  // ── Layout-aware sidebar style ──────────────────────────────────────────────
  const sidebarBg = (() => {
    if (isLightSidebar) return { background: "#ffffff" };
    switch (layout) {
      case DASHBOARD_LAYOUTS.GLASSMORPHISM:
        return {
          background: `${hexToRgba(darkPrimary, 0.82)}`,
          backdropFilter: "blur(18px)",
          WebkitBackdropFilter: "blur(18px)",
        };
      case DASHBOARD_LAYOUTS.MINIMAL:
        return { background: "#1e1e2e" };
      case DASHBOARD_LAYOUTS.MODERN_GRADIENT:
        return { background: `linear-gradient(160deg, ${darkenHex(primary, 0.85)} 0%, ${darkenHex(primary, 0.5)} 60%, ${primary} 100%)` };
      default:
        return { background: `linear-gradient(180deg, ${darkPrimary} 0%, ${primary} 100%)` };
    }
  })();

  // ── Layout-aware header style ───────────────────────────────────────────────
  const headerBg = (() => {
    switch (layout) {
      case DASHBOARD_LAYOUTS.GLASSMORPHISM:
        return {
          background: "rgba(255,255,255,0.82)",
          backdropFilter: "blur(14px)",
          WebkitBackdropFilter: "blur(14px)",
          borderBottom: "1px solid rgba(255,255,255,0.3)",
          boxShadow: "0 1px 0 rgba(0,0,0,0.04)",
        };
      case DASHBOARD_LAYOUTS.MODERN_GRADIENT:
        return {
          background: `linear-gradient(90deg, ${hexToRgba(primary, 0.03)}, #fff 40%)`,
          borderBottom: "1px solid #f0f0f0",
        };
      case DASHBOARD_LAYOUTS.MINIMAL:
        return { background: "#fff", borderBottom: "1px solid #e8e8e8", boxShadow: "none" };
      default:
        return { background: "#fff", borderBottom: "1px solid #f0f0f0", boxShadow: "0 1px 3px rgba(0,0,0,0.04)" };
    }
  })();

  const userMenuItems = [
    {
      key: "profile",
      label: (
        <div style={{ padding: "6px 0" }}>
          <p style={{ fontSize: 13, fontWeight: 700, color: "#111", margin: "0 0 1px" }}>{displayName}</p>
          <p style={{ fontSize: 11, color: "#888", margin: 0 }}>{emailAddress}</p>
        </div>
      ),
      disabled: true,
    },
    { type: "divider" },
    {
      key: "banking",
      icon: <ArrowLeftOutlined />,
      label: "Back to Banking",
      onClick: () => navigate("/dashboard"),
    },
  ];

  return (
    <Layout style={{ minHeight: "100vh", background: "#f6f7f9" }}>
      {/* ── Sidebar ─────────────────────────────────────────────────────────── */}
      <Sider
        collapsible collapsed={collapsed} onCollapse={setCollapsed} trigger={null}
        width={270} collapsedWidth={64}
        style={{
          ...sidebarBg,
          position: "fixed", top: 0, left: 0, height: "100vh", zIndex: 100,
          boxShadow: isLightSidebar ? "2px 0 8px rgba(0,0,0,0.06)" : layout === DASHBOARD_LAYOUTS.MINIMAL ? "none" : "2px 0 16px rgba(0,0,0,0.1)",
          borderRight: isLightSidebar ? "1px solid #f0f0f0" : "none",
          transition: "all 0.2s",
        }}
      >
        {/* Logo / brand */}
        <div style={{ padding: collapsed ? "18px 0" : "18px 16px 14px", borderBottom: isLightSidebar ? "1px solid #f0f0f0" : "1px solid rgba(255,255,255,0.1)" }}>
          {!collapsed ? (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
                <div style={{ width: 30, height: 30, borderRadius: 8, background: isLightSidebar ? hexToRgba(primary, 0.12) : "rgba(255,255,255,0.15)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14 }}>
                  🔑
                </div>
                <div>
                  <p style={{ fontSize: 13, fontWeight: 800, color: isLightSidebar ? primary : "#fff", margin: 0, letterSpacing: "-0.3px" }}>UltimateAgent</p>
                  <p style={{ fontSize: 9, color: isLightSidebar ? "#aaa" : "rgba(255,255,255,0.4)", margin: 0, letterSpacing: "0.5px", textTransform: "uppercase" }}>Property Management</p>
                </div>
              </div>
              <button onClick={() => setCollapsed(true)} style={{ background: "none", border: "none", cursor: "pointer", color: isLightSidebar ? "#bbb" : "rgba(255,255,255,0.5)", display: "flex", padding: 2, transition: "color 0.15s" }}>
                <MenuFoldOutlined style={{ fontSize: 13 }} />
              </button>
            </div>
          ) : (
            <div style={{ display: "flex", justifyContent: "center" }}>
              <button onClick={() => setCollapsed(false)} style={{ background: "none", border: "none", cursor: "pointer", color: isLightSidebar ? primary : "rgba(255,255,255,0.7)", display: "flex" }}>
                <MenuUnfoldOutlined style={{ fontSize: 15 }} />
              </button>
            </div>
          )}
        </div>

        {/* Nav items */}
        <div style={{ overflowY: "auto", padding: "6px 0", height: "calc(100vh - 130px)" }}>
          {navItems.map((item, i) => {
            if (item.type === "divider") {
              return !collapsed ? (
                <div key={i} style={{ padding: "14px 16px 4px", fontSize: 9, fontWeight: 700, letterSpacing: "1.6px", color: isLightSidebar ? "#bbb" : "rgba(255,255,255,0.28)", textTransform: "uppercase" }}>
                  {item.label}
                </div>
              ) : <div key={i} style={{ height: 10 }} />;
            }
            const isActive = activeKey === item.key;
            return (
              <div
                key={item.key}
                onClick={() => navigate(item.key)}
                style={{
                  display: "flex", alignItems: "center", gap: collapsed ? 0 : 10,
                  padding: collapsed ? "10px 0" : "8px 14px 8px 16px",
                  justifyContent: collapsed ? "center" : "flex-start",
                  cursor: "pointer",
                  margin: collapsed ? 0 : "1px 8px 1px 0",
                  borderRadius: collapsed ? 0 : "0 10px 10px 0",
                  background: isActive
                    ? isLightSidebar ? hexToRgba(primary, 0.1) : "rgba(255,255,255,0.16)"
                    : "transparent",
                  borderRight: isActive
                    ? isLightSidebar ? `3px solid ${primary}` : "3px solid rgba(255,255,255,0.9)"
                    : "3px solid transparent",
                  transition: "all 0.1s",
                }}
                onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.background = isLightSidebar ? hexToRgba(primary, 0.05) : "rgba(255,255,255,0.07)"; }}
                onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.background = "transparent"; }}
              >
                <span style={{ fontSize: 14, color: isActive ? (isLightSidebar ? primary : "#fff") : (isLightSidebar ? "#9ca3af" : "rgba(255,255,255,0.6)"), flexShrink: 0 }}>{item.icon}</span>
                {!collapsed && (
                  <>
                    <span style={{ fontSize: 12.5, fontWeight: isActive ? 700 : 400, color: isActive ? (isLightSidebar ? primary : "#fff") : (isLightSidebar ? "#374151" : "rgba(255,255,255,0.72)"), flex: 1, letterSpacing: isActive ? "-0.1px" : 0 }}>
                      {item.label}
                    </span>
                    {item.badge && (
                      <span style={{ background: "#ef4444", color: "#fff", fontSize: 10, fontWeight: 800, borderRadius: 10, padding: "1px 6px", lineHeight: 1.7, letterSpacing: 0 }}>
                        {item.badge}
                      </span>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>

        {/* Back to banking */}
        <div
          onClick={() => navigate("/dashboard")}
          style={{
            display: "flex", alignItems: "center", gap: 8,
            padding: collapsed ? "14px 0" : "12px 16px",
            justifyContent: collapsed ? "center" : "flex-start",
            cursor: "pointer",
            borderTop: isLightSidebar ? "1px solid #f0f0f0" : "1px solid rgba(255,255,255,0.1)",
            color: isLightSidebar ? "#9ca3af" : "rgba(255,255,255,0.45)", fontSize: 12, fontWeight: 400,
            transition: "color 0.15s",
          }}
          onMouseEnter={(e) => { e.currentTarget.style.color = isLightSidebar ? primary : "rgba(255,255,255,0.9)"; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = isLightSidebar ? "#9ca3af" : "rgba(255,255,255,0.45)"; }}
        >
          <ArrowLeftOutlined style={{ fontSize: 11 }} />
          {!collapsed && <span>Back to Banking</span>}
        </div>
      </Sider>

      {/* ── Main area ────────────────────────────────────────────────────────── */}
      <Layout style={{ marginLeft: collapsed ? 64 : 270, transition: "margin-left 0.2s" }}>
        {/* Header */}
        <Header style={{
          ...headerBg,
          padding: "0 20px 0 24px",
          height: 54, lineHeight: "54px",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          position: "sticky", top: 0, zIndex: 90,
        }}>
          {/* Left: breadcrumb */}
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ fontSize: 12, color: "#bbb", fontWeight: 500 }}>UltimateAgent</span>
            <span style={{ color: "#e0e0e0", fontSize: 12 }}>/</span>
            <span style={{ fontSize: 13, fontWeight: 700, color: "#1a1a1a" }}>
              {navItems.find((i) => i.key === activeKey)?.label || "Overview"}
            </span>
          </div>

          {/* Right: role switcher + layout switcher + user */}
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>

            {/* Role switcher */}
            <div style={{ display: "flex", background: "#f4f5f7", borderRadius: 24, padding: "3px 4px", gap: 2 }}>
              {UA_ROLES.map((r) => {
                const on = activeRole === r.key;
                return (
                  <button
                    key={r.key}
                    onClick={() => setActiveRole(r.key)}
                    style={{
                      padding: "4px 14px", borderRadius: 20, border: "none",
                      background: on ? primary : "transparent",
                      color: on ? "#fff" : "#888",
                      fontWeight: on ? 700 : 400,
                      fontSize: 11.5, cursor: "pointer",
                      transition: "all 0.15s",
                      letterSpacing: on ? "-0.1px" : 0,
                      boxShadow: on ? `0 2px 6px ${hexToRgba(primary, 0.3)}` : "none",
                    }}
                  >
                    {r.label}
                  </button>
                );
              })}
            </div>

            {/* Layout switcher — same component as banking side */}
            <DashboardLayoutSwitcher />

            {/* User display */}
            <Dropdown menu={{ items: userMenuItems }} trigger={["click"]} placement="bottomRight">
              <div style={{
                display: "flex", alignItems: "center", gap: 8, cursor: "pointer",
                padding: "5px 8px 5px 5px", borderRadius: 10,
                border: "1px solid #f0f0f0", background: "#fff",
                transition: "border-color 0.12s",
              }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = hexToRgba(primary, 0.3); }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#f0f0f0"; }}
              >
                <Avatar
                  size={26}
                  style={{ background: `linear-gradient(135deg, ${darkPrimary}, ${primary})`, fontSize: 10, fontWeight: 800, flexShrink: 0, lineHeight: "26px" }}
                >
                  {userInitials}
                </Avatar>
                <div style={{ lineHeight: 1.2, maxWidth: 130 }}>
                  <p style={{ fontSize: 12, fontWeight: 700, color: "#1a1a1a", margin: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {displayName}
                  </p>
                  <p style={{ fontSize: 10, color: "#999", margin: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {emailAddress || activeRole.charAt(0) + activeRole.slice(1).toLowerCase()}
                  </p>
                </div>
              </div>
            </Dropdown>
          </div>
        </Header>

        <Content style={{ background: "#f6f7f9", minHeight: "calc(100vh - 54px)" }}>
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
};

const UltimateAgentLayout = () => (
  <UltimateAgentProvider>
    <UltimateAgentShell />
  </UltimateAgentProvider>
);

export default UltimateAgentLayout;
